import { useState } from "react";
import {
  Box,
  Column,
  EmptyState,
  Row,
  SearchInput,
  Skeleton,
  SkeletonBox,
  Text,
} from "@hightouch/htui";
import { useDebounce } from "use-debounce";

import {
  AssetGrid,
  AssetGridSkeleton,
  LoadMoreFooter,
} from "src/components/asset-picker/asset-picker-primitives";

type EmptyStateCopy = {
  title: string;
  message: string;
  /** A way out of a dead end, e.g. widening a folder-scoped search. */
  actions?: React.ReactNode;
};
type PaneSlot = React.ReactNode | ((hasSearch: boolean) => React.ReactNode);

function renderPaneSlot(slot: PaneSlot | undefined, hasSearch: boolean) {
  return typeof slot === "function" ? slot(hasSearch) : slot;
}

/**
 * Everything a source's data hook must expose for the pane to render. Both
 * `useCatalog` and `useLibraryImages` already return this shape; a new source
 * (e.g. components) just needs a hook that does the same.
 */
export type AssetPaneData<T> = {
  items: T[];
  /** Omit when the source has no total; the count line is then hidden. */
  totalCount?: number;
  isTotalCountCapped?: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  /**
   * Query failure message. Rendered above the grid so a failed load doesn't
   * read as an empty source.
   */
  error?: string | null;
};

/** What the pane needs to lay out the items it has loaded. */
type LoadedPage<T> = Pick<
  AssetPaneData<T>,
  "items" | "hasMore" | "isLoadingMore" | "loadMore"
>;

export type AssetPaneResults<T> =
  | {
      /** One card per item in the shared grid, with a load-more footer. */
      layout: "grid";
      renderItem: (item: T) => React.ReactNode;
      /** Items per row. Drop to 3 when a preview panel narrows the grid area. */
      columns?: number;
    }
  | {
      /** The caller lays the page out and pages it, such as an infinite grid or a table. */
      layout: "custom";
      render: (page: LoadedPage<T>) => React.ReactNode;
    };

type Props<T> = {
  /** Singular noun for the count line, e.g. "product". */
  label: string;
  /** Plural noun for the header and "Showing X of Y {plural}" footer. */
  pluralLabel: string;
  searchPlaceholder: string;
  /**
   * The source's data hook, called with the current debounced search term. The
   * pane owns the search box; the caller owns the data. Must be a hook — it is
   * called unconditionally on every render.
   */
  useData: (debouncedSearch: string) => AssetPaneData<T>;
  /** Empty-state copy, keyed on whether a search is active. */
  emptyState: (hasSearch: boolean) => EmptyStateCopy;
  /** How the loaded items are laid out. */
  results: AssetPaneResults<T>;
  /** Controls rendered below the search input. */
  filters?: PaneSlot;
  /** Current folder path rendered beside the result heading. */
  breadcrumbs?: PaneSlot;
  /** Content rendered above the asset grid, such as child folder tiles. */
  beforeGrid?: PaneSlot;
  /** Controls at the right of the result heading that act on the items shown, such as selecting them all. Hidden while there are none. */
  headerActions?: (shown: { items: T[]; hasMore: boolean }) => React.ReactNode;
  /**
   * Cold-load placeholder. Defaults to the portrait card grid — override when
   * the cards aren't portrait (e.g. one-column full-width rows, which the
   * card aspect ratio would blow up to a full screen each).
   */
  renderSkeleton?: () => React.ReactNode;
};

/**
 * Shared search pane for the asset picker: a fixed header (search box + result
 * count) above a scrolling grid with a load-more footer. Both the product and
 * image sources render identically through this — the caller supplies the data
 * hook and the per-card renderer.
 */
export const AssetSearchPane = <T,>({
  label,
  pluralLabel,
  searchPlaceholder,
  useData,
  emptyState,
  results,
  filters,
  breadcrumbs,
  beforeGrid,
  headerActions,
  renderSkeleton,
}: Props<T>) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);

  const {
    items,
    totalCount,
    isTotalCountCapped = false,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    error,
  } = useData(debouncedSearch);

  // Skeleton only on a cold load (no cached data for this query yet). A new
  // search changes the query key, so it too reports `isLoading`. Background
  // refetches of an already-loaded key keep whatever is on screen — existing
  // items or the empty state — instead of flashing skeletons over it.
  const showSkeleton = isLoading;
  const hasSearch = Boolean(debouncedSearch);
  const copy = emptyState(hasSearch);
  const resolvedFilters = renderPaneSlot(filters, hasSearch);
  const resolvedBreadcrumbs = renderPaneSlot(breadcrumbs, hasSearch);
  const resolvedBeforeGrid = renderPaneSlot(beforeGrid, hasSearch);

  return (
    <Column gap={4} height="100%" minHeight={0}>
      {/* Fixed header: search + results count. Only the grid below scrolls. */}
      <Column gap={4} flexShrink={0}>
        <SearchInput
          width="100%"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {resolvedFilters}

        <Box
          display="flex"
          alignItems={{ base: "flex-start", sm: "center" }}
          justifyContent="space-between"
          gap={3}
          minWidth={0}
          flexDirection={{ base: "column", sm: "row" }}
        >
          <Row align="center" gap={2} minWidth={0}>
            <Text fontWeight="medium">
              {debouncedSearch ? "Results" : `All ${pluralLabel}`}
            </Text>
            {/* Skeleton under the same condition as the grid, so a new search
                can't flash "0" while the query is in flight. */}
            {showSkeleton ? (
              <Skeleton isLoading={showSkeleton}>
                <SkeletonBox height={4} width={20} borderRadius="sm" />
              </Skeleton>
            ) : totalCount !== undefined ? (
              <Text size="sm" color="text.secondary" mt={0.5}>
                {totalCount} {totalCount === 1 ? label : pluralLabel}
              </Text>
            ) : null}
          </Row>
          {resolvedBreadcrumbs}
          {!showSkeleton &&
            items.length > 0 &&
            headerActions?.({ items, hasMore })}
        </Box>
      </Column>

      <Column flex={1} minHeight={0} overflowY="auto" gap={4}>
        {resolvedBeforeGrid}
        {error && (
          <Text size="sm" color="text.danger">
            {error}
          </Text>
        )}
        {showSkeleton ? (
          (renderSkeleton?.() ?? (
            <AssetGridSkeleton
              columns={results.layout === "grid" ? results.columns : undefined}
            />
          ))
        ) : items.length === 0 && !resolvedBeforeGrid ? (
          <EmptyState
            title={copy.title}
            message={copy.message}
            actions={copy.actions}
          />
        ) : items.length > 0 ? (
          results.layout === "custom" ? (
            results.render({ items, hasMore, isLoadingMore, loadMore })
          ) : (
            <>
              <AssetGrid columns={results.columns}>
                {items.map((item) => results.renderItem(item))}
              </AssetGrid>
              <LoadMoreFooter
                shownCount={items.length}
                totalCount={totalCount}
                isTotalCountCapped={isTotalCountCapped}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                onLoadMore={loadMore}
                label={pluralLabel}
              />
            </>
          )
        ) : null}
      </Column>
    </Column>
  );
};
